import React, { FC, ReactNode } from 'react';
import { Button, ButtonVariant } from '../button/Button';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';

// ---------------------------------------------------------------------------
// WizardSummaryLayout
// ---------------------------------------------------------------------------
// Owns the outer shell, scrollable content area, and footer button row.
//
// Usage:
//   <WizardSummaryLayout onBack={onBack} submitLabel={translate.byKey(TranslationKeys.confirm)}>
//     <WizardSummarySection heading="Task Details">
//       <WizardSummaryField label="Task" value={taskDescription} />
//     </WizardSummarySection>
//   </WizardSummaryLayout>
//
// Back button is always "Back" (sourced from the translation context internally).
// Submit button label is always passed as a prop — forces a conscious choice per wizard.
//
// Tertiary buttons (e.g. "Scan Network") are passed via the `tertiaryActions` prop
// and are rendered on the LEFT of the footer. Back/Submit remain on the RIGHT.
// ---------------------------------------------------------------------------

interface WizardSummaryLayoutProps {
    /** The scrollable summary content */
    children: ReactNode;
    onBack?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Translated label for the primary action button (confirm / submit / install / execute …) */
    submitLabel: string;
    /**
     * Called on primary button click.
     * When omitted the button renders as type="submit" for Formik forms.
     */
    onSubmit?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Optional actions rendered on the LEFT of the footer (e.g. Download, Scan Network) */
    tertiaryActions?: ReactNode;
    submitDisabled?: boolean;
}

export const WizardSummaryLayout: FC<WizardSummaryLayoutProps> = ({
    children,
    onBack,
    submitLabel,
    onSubmit,
    tertiaryActions,
    submitDisabled = false,
}) => {
    const { translate } = useLocale();

    return (
        <div className="flex flex-col pt-3 pr-3 h-full relative">
            {/* Scrollable content — mb-16 prevents content from hiding behind the fixed footer */}
            <div className="flex-1 overflow-y-auto mb-16">
                {children}
            </div>

            {/* Footer — always full-width so tertiary actions can sit on the left */}
            <div className={`flex flex-row items-center absolute bottom-0 left-0 right-0 px-3 pb-3 ${tertiaryActions ? 'justify-between' : 'justify-end'}`}>
                {tertiaryActions && (
                    <div className="flex flex-row mt-4">
                        {tertiaryActions}
                    </div>
                )}
                <div className="flex flex-row gap-3 mt-4">
                    <Button variant="outline" invisible={!onBack} onClick={onBack}>
                        {translate.byKey(TranslationKeys.back)}
                    </Button>
                    <Button
                        variant="submit"
                        type={onSubmit ? 'button' : 'submit'}
                        disabled={submitDisabled}
                        onClick={onSubmit}
                    >
                        {submitLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// WizardSummarySection
// ---------------------------------------------------------------------------
// Groups related fields under a bold dark-grey heading (not the blue title).
// Matches the "Task Details" / "Enrolment Details" pattern from Enrol Credentials.
//
// Usage:
//   <WizardSummarySection heading="Enrolment Details">
//     <WizardSummaryField label="Tag Code" value={values.tagDisplay} />
//   </WizardSummarySection>
// ---------------------------------------------------------------------------

interface WizardSummarySectionProps {
    heading: string;
    children: ReactNode;
}

export const WizardSummarySection: FC<WizardSummarySectionProps> = ({ heading, children }) => (
    <div className="flex flex-col pt-3">
        <h2 className="p-1 text-lg font-bold">{heading}:</h2>
        {children}
    </div>
);

// ---------------------------------------------------------------------------
// WizardSummaryField
// ---------------------------------------------------------------------------
// A single label / value row.  Label is small grey text; value is slightly
// larger black text — matching the established Enrol Credentials pattern.
//
// Usage:
//   <WizardSummaryField label="Status" value={getStatusName(values.statusId, translate)} />
//   <WizardSummaryField label="Devices" value={<ul>…</ul>} />   ← rich values are fine
// ---------------------------------------------------------------------------

interface WizardSummaryFieldProps {
    label: string;
    value: ReactNode;
}

export const WizardSummaryField: FC<WizardSummaryFieldProps> = ({ label, value }) => (
    <div className="p-1 text-xs text-gray-600">
        {label}: <span className="text-sm text-black">{value}</span>
    </div>
);

// ---------------------------------------------------------------------------
// WizardStepLayout
// ---------------------------------------------------------------------------
// Shell for non-summary wizard step pages (device selection, form pages, etc.).
// Owns the outer wrapper and footer only — content manages its own internal
// height (e.g. tables with constrained maxHeight inside ActivityFrame).
//
// Back/Next are always on the RIGHT. Tertiary actions (Scan Subnet, Scan Bus,
// Advanced Scan, etc.) are passed via `tertiaryActions` and sit on the LEFT.
//
// Button classes used:
//   Back     → variant="outline"   (pill, white fill, themed border/text)
//   Next     → variant="primary"   (pill, theme-colour fill, white text)
//   Tertiary → variant="tertiary"  (pill, dark fill, white text) — pass as <Button> inside tertiaryActions
//
// Usage:
//   <WizardStepLayout
//     onBack={onBack}
//     onNext={handleNext}
//     nextDisabled={!isValid}
//     tertiaryActions={<Button variant="tertiary" onClick={scan}>Scan Subnet</Button>}
//   >
//     <ActivityFrame ...>...</ActivityFrame>
//   </WizardStepLayout>
// ---------------------------------------------------------------------------

interface WizardStepLayoutProps {
    children: ReactNode;
    /** Back handler. Pass undefined on the first step — button renders invisible to preserve spacing. */
    onBack?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Overrides the default "Next" translation */
    nextLabel?: string;
    nextDisabled?: boolean;
    /** Overrides the default "primary" variant for the next/confirm button */
    nextVariant?: ButtonVariant;
    /** Rendered on the LEFT of the footer (e.g. Scan Subnet, Scan Bus, Advanced Scan) */
    tertiaryActions?: ReactNode;
}

export const WizardStepLayout: FC<WizardStepLayoutProps> = ({
    children,
    onBack,
    onNext,
    nextLabel,
    nextDisabled = false,
    nextVariant = 'primary',
    tertiaryActions,
}) => {
    const { translate } = useLocale();

    return (
        <div className="flex flex-col pt-3 pr-3 h-full relative">
            <div className="flex-1 overflow-y-auto mb-16">
                {children}
            </div>

            <div className={`flex flex-row items-center absolute bottom-0 left-0 right-0 px-3 pb-3 ${tertiaryActions ? 'justify-between' : 'justify-end'}`}>
                {tertiaryActions && (
                    <div className="flex flex-row gap-2 mt-3">
                        {tertiaryActions}
                    </div>
                )}
                <div className="flex flex-row gap-3 mt-3">
                    <Button variant="outline" invisible={!onBack} onClick={onBack}>
                        {translate.byKey(TranslationKeys.back)}
                    </Button>
                    <Button
                        variant={nextVariant}
                        disabled={nextDisabled}
                        onClick={onNext}
                    >
                        {nextLabel ?? translate.byKey(TranslationKeys.next)}
                    </Button>
                </div>
            </div>
        </div>
    );
};
